import { Copy } from "lucide-react";
import { type ReactNode, useState } from "react";
import { IconButton } from "../atoms/IconButton";
import { cn } from "../lib/cn";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "../primitives/Tooltip";

export interface ProofEntry {
	hash: string;
	position: "left" | "right";
}

export interface MerkleTreeSVGProps {
	leafHash: string;
	proof: ProofEntry[];
	merkleRoot: string;
	step: number;
	onStepChange: (step: number) => void;
	/**
	 * Whether the record verified successfully. Gates the success colour of the
	 * root node so a not-yet-verified record is never painted falsely green.
	 */
	verified?: boolean;
	className?: string;
}

// Layout constants
const NODE_W = 120;
const NODE_H = 40;
const NODE_RX = 6;
const LEVEL_GAP = 80;
const COL_GAP = 160;
const PAD_X = 20;
const PAD_Y = 10;

type NodeRole = "leaf" | "sibling" | "computed" | "root";

interface LayoutNode {
	id: string;
	hash: string;
	role: NodeRole;
	level: number; // 0 = leaf row, proof.length = root row
	x: number;
	y: number;
}

interface Edge {
	x1: number;
	y1: number;
	x2: number;
	y2: number;
	onPath: boolean;
}

function truncateHash(value: string): string {
	if (value.length <= 14) {
		return value;
	}
	return `${value.slice(0, 6)}…${value.slice(-4)}`;
}

function buildLayout(
	leafHash: string,
	proof: ProofEntry[],
	merkleRoot: string,
	step: number,
): { nodes: LayoutNode[]; edges: Edge[]; svgW: number; svgH: number } {
	const depth = proof.length + 1;
	const totalHeight = depth * (NODE_H + LEVEL_GAP) - LEVEL_GAP;
	const totalWidth = COL_GAP + NODE_W;
	const centerX = totalWidth / 2 + PAD_X;
	const svgW = totalWidth + NODE_W + PAD_X * 2;
	const svgH = totalHeight + NODE_H + PAD_Y * 2;

	const nodes: LayoutNode[] = [];
	const edges: Edge[] = [];

	// Root node at top
	const rootY = PAD_Y;
	nodes.push({
		id: "root",
		hash: merkleRoot,
		role: "root",
		level: proof.length,
		x: centerX - NODE_W / 2,
		y: rootY,
	});

	// Each proof level (i=0 is the leaf row)
	for (let i = 0; i < proof.length; i++) {
		const proofEntry = proof[i];
		if (proofEntry === undefined) {
			continue;
		}

		const y = PAD_Y + totalHeight - i * (NODE_H + LEVEL_GAP);
		// Only the leaf row carries a genuine hash. Higher rows are intermediate
		// results this presentational component must not fabricate, so they hold
		// no hash and are labelled "Combined" by NodeRect.
		const currentHash = i === 0 ? leafHash : "";

		// sibling position tells us which side the sibling is on
		const siblingIsRight = proofEntry.position === "right";
		const currX = siblingIsRight
			? centerX - COL_GAP / 2 - NODE_W
			: centerX + COL_GAP / 2;
		const sibX = siblingIsRight
			? centerX + COL_GAP / 2
			: centerX - COL_GAP / 2 - NODE_W;

		const nodeRole: NodeRole = i === 0 ? "leaf" : "computed";

		nodes.push({
			id: `current-${i}`,
			hash: currentHash,
			role: nodeRole,
			level: i,
			x: currX,
			y,
		});

		nodes.push({
			id: `sibling-${i}`,
			hash: proofEntry.hash,
			role: "sibling",
			level: i,
			x: sibX,
			y,
		});

		// Edges going up to parent
		const parentY =
			i === proof.length - 1
				? rootY + NODE_H
				: PAD_Y + totalHeight - (i + 1) * (NODE_H + LEVEL_GAP) + NODE_H;

		edges.push({
			x1: currX + NODE_W / 2,
			y1: y,
			x2: centerX,
			y2: parentY,
			onPath: i < step,
		});
		edges.push({
			x1: sibX + NODE_W / 2,
			y1: y,
			x2: centerX,
			y2: parentY,
			onPath: false,
		});
	}

	return { nodes, edges, svgW, svgH };
}

function NodeRect({
	node,
	step,
	proofLength,
	verified,
}: {
	node: LayoutNode;
	step: number;
	proofLength: number;
	verified: boolean;
}): ReactNode {
	const [, setCopied] = useState(false);

	function handleCopy(): void {
		navigator.clipboard
			.writeText(node.hash)
			.then(() => {
				setCopied(true);
				setTimeout(() => setCopied(false), 1500);
			})
			.catch(() => undefined);
	}

	const isRoot = node.role === "root";
	const isSibling = node.role === "sibling";
	const isComputed = node.role === "computed";
	const isCurrentLevel = node.level === step && !isRoot;
	const isBelowStep = node.level < step && !isRoot;
	const rootResolved = isRoot && step === proofLength;

	let rectClass: string;
	if (rootResolved) {
		rectClass = verified ? "fill-statusSuccess" : "fill-muted";
	} else if (isRoot) {
		rectClass = "fill-muted";
	} else if (isSibling && isCurrentLevel) {
		rectClass = "fill-accent";
	} else if (!isSibling && (isCurrentLevel || isBelowStep)) {
		rectClass = "fill-primary";
	} else {
		rectClass = "fill-muted";
	}

	const textIsBright =
		(!isSibling && (isCurrentLevel || isBelowStep)) ||
		(rootResolved && verified);
	const textClass = textIsBright
		? "fill-primaryForeground"
		: "fill-mutedForeground";

	// Computed nodes have no genuine hash. Show a non-deceptive label and skip
	// the copy affordance — there is nothing real to copy.
	if (isComputed) {
		return (
			<g aria-label={`Combined result at step ${node.level}`}>
				<rect
					x={node.x}
					y={node.y}
					width={NODE_W}
					height={NODE_H}
					rx={NODE_RX}
					className={cn(
						rectClass,
						"stroke-border stroke-1",
						"[transition:fill_200ms_ease-out]",
					)}
				/>
				<text
					x={node.x + NODE_W / 2}
					y={node.y + NODE_H / 2 + 4}
					textAnchor="middle"
					fontSize={10}
					className={cn(
						textClass,
						"select-none [transition:fill_200ms_ease-out]",
					)}
				>
					Combined
				</text>
			</g>
		);
	}

	let ariaLabel: string;
	if (node.id === "current-0") {
		ariaLabel = `Leaf hash ${node.hash.slice(0, 10)}`;
	} else if (isRoot) {
		ariaLabel = `Merkle root ${node.hash.slice(0, 10)}`;
	} else {
		ariaLabel = `Sibling at level ${node.level + 1} (${node.hash.slice(0, 10)})`;
	}

	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger asChild={true}>
					<g aria-label={ariaLabel}>
						<rect
							x={node.x}
							y={node.y}
							width={NODE_W}
							height={NODE_H}
							rx={NODE_RX}
							className={cn(
								rectClass,
								"stroke-border stroke-1",
								"[transition:fill_200ms_ease-out]",
							)}
						/>
						<text
							x={node.x + NODE_W / 2}
							y={node.y + NODE_H / 2 + 4}
							textAnchor="middle"
							fontSize={10}
							fontFamily="monospace"
							className={cn(
								textClass,
								"select-none [transition:fill_200ms_ease-out]",
							)}
						>
							{truncateHash(node.hash)}
						</text>
					</g>
				</TooltipTrigger>
				<TooltipContent side="top">
					<div className="flex items-center gap-2 max-w-xs">
						<span className="font-mono text-xs break-all">{node.hash}</span>
						<IconButton
							aria-label="Copy hash"
							size="sm"
							variant="ghost"
							onClick={handleCopy}
						>
							<Copy className="h-3 w-3" aria-hidden="true" />
						</IconButton>
					</div>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}

function MerkleTreeSVG({
	leafHash,
	proof,
	merkleRoot,
	step,
	verified = true,
	className,
}: MerkleTreeSVGProps): ReactNode {
	// Degenerate: no proof steps — leaf is the root
	if (proof.length === 0) {
		const svgW = NODE_W + PAD_X * 2;
		const svgH = NODE_H + PAD_Y * 2;
		return (
			<div className={cn("overflow-x-auto", className)}>
				<svg
					viewBox={`0 0 ${svgW} ${svgH}`}
					width={svgW}
					height={svgH}
					aria-label="Merkle proof tree"
					role="img"
				>
					<g aria-label={`Leaf hash ${leafHash.slice(0, 10)}`}>
						<rect
							x={PAD_X}
							y={PAD_Y}
							width={NODE_W}
							height={NODE_H}
							rx={NODE_RX}
							className="fill-primary stroke-border stroke-1"
						/>
						<text
							x={PAD_X + NODE_W / 2}
							y={PAD_Y + NODE_H / 2 + 4}
							textAnchor="middle"
							fontSize={10}
							fontFamily="monospace"
							className="fill-primaryForeground select-none"
						>
							{truncateHash(leafHash)}
						</text>
					</g>
				</svg>
			</div>
		);
	}

	const { nodes, edges, svgW, svgH } = buildLayout(
		leafHash,
		proof,
		merkleRoot,
		step,
	);

	return (
		<div className={cn("overflow-x-auto", className)}>
			<svg
				viewBox={`0 0 ${svgW} ${svgH}`}
				width={svgW}
				height={svgH}
				aria-label="Merkle proof tree"
				role="img"
			>
				{edges.map((edge, i) => (
					<line
						// biome-ignore lint/suspicious/noArrayIndexKey: edges have no natural key; order is stable
						key={`edge-${i}`}
						x1={edge.x1}
						y1={edge.y1}
						x2={edge.x2}
						y2={edge.y2}
						strokeWidth={2}
						className={cn(
							edge.onPath ? "stroke-primary" : "stroke-border",
							"[transition:stroke_200ms_ease-out]",
						)}
					/>
				))}
				{nodes.map((node) => (
					<NodeRect
						key={node.id}
						node={node}
						step={step}
						proofLength={proof.length}
						verified={verified}
					/>
				))}
			</svg>
		</div>
	);
}

MerkleTreeSVG.displayName = "MerkleTreeSVG";

export default MerkleTreeSVG;
export { MerkleTreeSVG };
